import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import API from "../../../api";
import { createDiscount, updateDiscount, getDiscount } from "../../../api/discounts";
import { fetchBranches } from "../../../api/branches";
import { listLevels } from "../../../api/levels";
import { formatLevelLabel } from "../../../utils/formatLevelLabel";
import { listBatches } from "../../../api/batches";
import Button from "../../../components/common/Button";
import PageHeaderSection from "../../../components/layout/PageHeaderSection";
import PageLoadingShell from "../../../components/layout/PageLoadingShell";
import { getCurrencySymbol } from "../../../utils/currency";
import { TextInput, SelectInput, TextareaInput, CheckboxField } from "../../../components/common/form";
import { apiErrorMessage } from '../../../utils/apiError';

export default function DiscountForm() {
    const { t } = useTranslation();
    const currencySymbol = getCurrencySymbol();
    const navigate = useNavigate();
    const { id } = useParams();
    const isEditMode = !!id;

    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    // Form fields
    const [code, setCode] = useState("");
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [discountType, setDiscountType] = useState("PERCENT");
    const [discountValue, setDiscountValue] = useState("");
    const [branchId, setBranchId] = useState("");
    const [levelId, setLevelId] = useState("");
    const [batchId, setBatchId] = useState("");
    const [isActive, setIsActive] = useState(true);

    // Master data
    const [branches, setBranches] = useState([]);
    const [levels, setLevels] = useState([]);
    const [batches, setBatches] = useState([]);
    const [scopeLoadError, setScopeLoadError] = useState('');

    useEffect(() => {
        loadMasterData();
        if (isEditMode) {
            loadDiscount();
        }
    }, [id]);

    const handleBranchChange = (e) => {
        const newBranchId = e.target.value;
        setBranchId(newBranchId);
        setLevelId("");
        setBatchId("");
        setLevels([]);
        setBatches([]);
        setScopeLoadError('');

        if (newBranchId) {
            loadLevels(newBranchId);
        }
    };

    const handleLevelChange = (e) => {
        const newLevelId = e.target.value;
        setLevelId(newLevelId);
        setBatchId("");
        setBatches([]);
        setScopeLoadError('');

        if (newLevelId) {
            loadBatches(branchId, newLevelId);
        }
    };

    const loadMasterData = async () => {
        try {
            const branchesRes = await fetchBranches();
            setBranches(Array.isArray(branchesRes.data) ? branchesRes.data : []);
            setScopeLoadError('');
        } catch (error) {
            console.error("Failed to load branches:", error);
            setBranches([]);
            setScopeLoadError(t('common.optionsLoadFailed'));
        }
    };

    const loadLevels = async (branchIdFilter) => {
        try {
            console.log("Loading levels for branchId:", branchIdFilter);
            let levelsData = [];

            if (branchIdFilter) {
                // Use the by-branch endpoint when filtering by branch
                const levelsRes = await API.get(`/admin/levels/levels/by-branch/${branchIdFilter}`);
                console.log("Levels response:", levelsRes);
                levelsData = Array.isArray(levelsRes.data) ? levelsRes.data : [];
            } else {
                // Use the regular list endpoint for all levels
                const levelsRes = await listLevels("ADMIN");
                console.log("Levels response:", levelsRes);
                levelsData = Array.isArray(levelsRes.data?.content) ? levelsRes.data.content :
                    Array.isArray(levelsRes.data) ? levelsRes.data : [];
            }

            console.log("Setting levels:", levelsData);
            setLevels(levelsData);
            setScopeLoadError('');
        } catch (error) {
            console.error("Failed to load levels:", error);
            setLevels([]);
            setScopeLoadError(t('common.optionsLoadFailed'));
        }
    };

    const loadBatches = async (branchIdFilter, levelIdFilter) => {
        try {
            console.log("Loading batches for branchId:", branchIdFilter, "levelId:", levelIdFilter);
            const batchesRes = await listBatches("ADMIN");
            console.log("Batches response:", batchesRes);

            let batchesData = Array.isArray(batchesRes.data) ? batchesRes.data : [];

            // Filter on client side since backend doesn't support filtering
            if (branchIdFilter) {
                batchesData = batchesData.filter(b =>
                    (b.branchId || b.branch?.id || b.branch?.branchId) == branchIdFilter
                );
            }
            if (levelIdFilter) {
                batchesData = batchesData.filter(b =>
                    (b.levelId || b.level?.id || b.level?.levelId) == levelIdFilter
                );
            }

            console.log("Setting batches:", batchesData);
            setBatches(batchesData);
            setScopeLoadError('');
        } catch (error) {
            console.error("Failed to load batches:", error);
            setBatches([]);
            setScopeLoadError(t('common.optionsLoadFailed'));
        }
    };

    const loadDiscount = async () => {
        setLoading(true);
        try {
            const res = await getDiscount(id);
            const discount = res.data;
            setCode(discount.code || "");
            setName(discount.name || "");
            setDescription(discount.description || "");
            setDiscountType(discount.discountType || "PERCENT");
            setDiscountValue(discount.discountValue || "");
            setBranchId(discount.branchId || "");
            setLevelId(discount.levelId || "");
            setBatchId(discount.batchId || "");
            setIsActive(discount.isActive !== false);

            // Load dependent data based on selected values
            if (discount.branchId) {
                await loadLevels(discount.branchId);
                if (discount.levelId) {
                    await loadBatches(discount.branchId, discount.levelId);
                }
            }
        } catch (error) {
            console.error("Failed to load discount:", error);
            alert(t("pages.discountForm.loadFailed"));
            navigate("/admin/discounts");
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!code.trim()) {
            alert(t("pages.discountForm.codeRequired"));
            return;
        }
        if (!name.trim()) {
            alert(t("pages.discountForm.nameRequired"));
            return;
        }
        if (!discountValue || Number(discountValue) <= 0) {
            alert(t("pages.discountForm.valueRequired"));
            return;
        }
        if (discountType === "PERCENT" && Number(discountValue) > 100) {
            alert(t('pages.discountForm.percentMaxAlert'));
            return;
        }

        const payload = {
            code: code.trim().toUpperCase(),
            name: name.trim(),
            description: description.trim() || null,
            discountType,
            discountValue: Number(discountValue),
            branchId: branchId ? Number(branchId) : null,
            levelId: levelId ? Number(levelId) : null,
            batchId: batchId ? Number(batchId) : null,
            isActive
        };

        setSubmitting(true);
        try {
            if (isEditMode) {
                await updateDiscount(id, payload);
                alert(t('pages.discountForm.updateSuccess'));
            } else {
                await createDiscount(payload);
                alert(t('pages.discountForm.createSuccess'));
            }
            navigate("/admin/discounts");
        } catch (error) {
            console.error("Failed to save discount:", error);
            alert(apiErrorMessage(error, t("pages.discountForm.saveFailed")));
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <PageLoadingShell
                variant="page-shell-form"
                title={isEditMode ? t("pages.discountForm.editTitle") : t("pages.discountForm.createTitle")}
                subtitle={isEditMode
                    ? t("pages.discountForm.editSubtitle")
                    : t("pages.discountForm.createSubtitle")}
            />
        );
    }

    return (
        <div className="page-shell-form">
            <PageHeaderSection
                backTo="/admin/discounts"
                title={isEditMode ? t("pages.discountForm.editTitle") : t("pages.discountForm.createTitle")}
                subtitle={isEditMode
                    ? t("pages.discountForm.editSubtitle")
                    : t("pages.discountForm.createSubtitle")}
            />

            <form onSubmit={handleSubmit} className="form-shell">
                {/* Code & Name */}
                <div className="form-section grid grid-cols-1 gap-3 md:grid-cols-2">
                    <TextInput
                        label={t("pages.discountForm.codeLabel")}
                        type="text"
                        value={code}
                        onChange={(e) => setCode(e.target.value.toUpperCase())}
                        className="font-mono"
                        placeholder={t('pages.discountForm.codePlaceholder')}
                        maxLength={50}
                        required
                    />

                    <TextInput
                        label={t("pages.discountForm.nameLabel")}
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder={t('pages.discountForm.namePlaceholder')}
                        maxLength={100}
                        required
                    />
                </div>

                {/* Description */}
                <div className="form-section">
                    <TextareaInput
                        label={t("pages.discountForm.descriptionLabel")}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder={t('pages.discountForm.optionalDescription')}
                        rows={3}
                        maxLength={255}
                    />
                </div>

                {/* Discount Type & Value */}
                <div className="form-section grid grid-cols-1 gap-3 md:grid-cols-2">
                    <SelectInput
                        label={t("pages.discountForm.typeLabel")}
                        value={discountType}
                        onChange={(e) => setDiscountType(e.target.value)}
                        required
                    >
                        <option value="PERCENT">{t('pages.discountForm.percentOption')}</option>
                        <option value="FLAT">{t('pages.discountForm.fixedOption', { currency: currencySymbol || t('common.currencyWord') })}</option>
                    </SelectInput>

                    <div className="relative">
                        <TextInput
                            label={t("pages.discountForm.valueLabel")}
                            type="number"
                            value={discountValue}
                            onChange={(e) => setDiscountValue(e.target.value)}
                            className="pr-10"
                            placeholder={discountType === "PERCENT"
                                ? t('pages.discountForm.percentValuePlaceholder')
                                : t('pages.discountForm.flatValuePlaceholder')}
                            min="0"
                            max={discountType === "PERCENT" ? "100" : undefined}
                            step={discountType === "PERCENT" ? "0.01" : "1"}
                            required
                        />
                        <span className="absolute right-4 top-[2.125rem] text-gray-400 text-sm pointer-events-none">
                            {discountType === "PERCENT" ? "%" : currencySymbol}
                        </span>
                    </div>
                </div>

                {/* Discount Scope */}
                <div className="form-section">
                    <h3 className="form-section-title">{t('pages.discountForm.scopeTitle')}</h3>
                    <p className="field-note mb-3">
                        {t('pages.discountForm.scopeHint')}
                    </p>
                    {scopeLoadError && (
                        <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800" role="status">
                            {scopeLoadError}
                        </div>
                    )}
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                        <SelectInput
                            label={t("pages.discountForm.branchLabel")}
                            value={branchId}
                            onChange={handleBranchChange}
                            disabled={!!scopeLoadError && branches.length === 0}
                        >
                            <option value="">{t("pages.discountForm.allBranches")}</option>
                            {branches.map(b => (
                                <option key={b.id || b.branchId} value={b.id || b.branchId}>
                                    {b.name || b.branchName}
                                </option>
                            ))}
                        </SelectInput>

                        <SelectInput
                            label={t("pages.discountForm.levelLabel")}
                            value={levelId}
                            onChange={handleLevelChange}
                            disabled={!branchId}
                        >
                            <option value="">{branchId ? t("common.all") : t("pages.discountForm.selectBranch")}</option>
                            {levels.map(l => (
                                <option key={l.id || l.levelId} value={l.id || l.levelId}>
                                    {formatLevelLabel(l)}
                                </option>
                            ))}
                        </SelectInput>

                        <SelectInput
                            label={t("pages.discountForm.batchLabel")}
                            value={batchId}
                            onChange={(e) => setBatchId(e.target.value)}
                            disabled={!levelId}
                        >
                            <option value="">{levelId ? t("common.all") : t("pages.discountForm.selectLevel")}</option>
                            {batches.map(b => (
                                <option key={b.id || b.batchId} value={b.id || b.batchId}>
                                    {b.name || b.batchName}
                                </option>
                            ))}
                        </SelectInput>
                    </div>
                </div>

                {/* Active Toggle */}
                <CheckboxField
                    layout="toggle-card"
                    label={t("pages.discountForm.activeLabel")}
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                />

                {/* Actions */}
                <div className="page-actions justify-end">
                    <Button
                        type="button"
                        variant="secondary"
                        onClick={() => navigate("/admin/discounts")}
                        disabled={submitting}
                    >
                        {t("common.cancel")}
                    </Button>
                    <Button
                        type="submit"
                        variant="gradient-primary"
                        disabled={submitting}
                    >
                        {submitting ? t("pages.discountForm.saving") : isEditMode ? t("pages.discountForm.update") : t("pages.discountForm.create")}
                    </Button>
                </div>
            </form>
        </div>
    );
}
